import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { factories, models, service } from 'powerbi-client';
import { useContext, useEffect, useRef, useState } from 'react';
import { PbToken } from 'services/home';
import styled, { keyframes } from 'styled-components';
import { FullscreenButton, LogoSpinner } from './styled';

// Estilos do carregamento
const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const pulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
`;

const pulseColor = keyframes`
    0% { box-shadow: 0 0 15px #0077CC; }
    40% { box-shadow: 0 0 25px #8E24AA; }
    80% { box-shadow: 0 0 25px #C678FF; }
    100% { box-shadow: 0 0 15px #0077CC; }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* NOVO: Efeito de luz radial sutil */
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(245, 245, 245, 0.95) 60%
  );
  /* Mantenha o fallback para navegadores antigos e a cor de fundo */
  background-color: rgba(245, 245, 245, 0.95);
  backdrop-filter: blur(
    5px
  ); /* Você pode ajustar o 'blur' se quiser mais foco na luz */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 100;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 25vw;
  height: 25vw;
  max-width: 400px;
  max-height: 400px;
  /* Linhas removidas para tirar o círculo branco e o efeito de pulsação de cor: */
  /* animation: ${pulseColor} 2s infinite ease-in-out; */
  /* border-radius: 50%; */
  /* Se quiser manter o efeito de 'pulseColor' e aplicá-lo ao 'ImageLoader' ou 'LogoSpinner', 
       você pode movê-lo, mas vou removê-lo do container principal para tirar o círculo.
       Se a sua intenção é que o CONTAINER PULSE, mas sem ser um círculo, remova apenas o 'border-radius'.
       Se a sua intenção é que nem o container pulse a cor (apenas as imagens girem/pulsem), remova as duas linhas comentadas.
       Mantenho as duas comentadas para focar em remover o círculo e o efeito que o emoldura. */

  @media (max-width: 768px) {
    width: 60vw;
    height: 60vw;
  }
`;

const ImageLoader = styled.img`
  width: 25vw; /* AUMENTADO: Era 20vw */
  height: 25vw; /* AUMENTADO: Era 20vw */
  max-width: 450px; /* AUMENTADO: Era 350px (Ajuste o valor máximo conforme sua necessidade) */
  max-height: 450px; /* AUMENTADO: Era 350px (Ajuste o valor máximo conforme sua necessidade) */
  object-fit: contain;
  animation: ${pulse} 2s infinite ease-in-out;
  z-index: 2;
  @media (max-width: 768px) {
    width: 60vw; /* AUMENTADO: Era 50vw (Ajuste para telas menores também) */
    height: 60vw; /* AUMENTADO: Era 50vw (Ajuste para telas menores também) */
  }
`;

// const LogoSpinner = styled.img`
//     display: flex;
//     position: absolute;
//     width: 25vw;
//     height: 25vw;
//     max-width: 470px;
//     max-height: 470px;
//     object-fit: contain; /* Alterado para contain para respeitar a forma redonda da imagem */
//     border-radius: 50%; /* Garante formato circular */
//     animation: ${spin} 6s linear infinite;
//     z-index: 1; /* Coloca a logo giratória atrás da estática */
//     @media (max-width: 768px) {
//         width: 60vw;
//         height: 60vw;
//     }
// `;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  position: absolute;
  bottom: 10%;
  width: 100%;
  text-align: center;
  padding: 0 1rem;
`;

const LoadingText = styled.p`
  font-size: 1.5rem;
  color: ${(props) => (props.status === 'error' ? '#d32f2f' : '#555')};
  font-weight: 500;
  opacity: 0;
  animation: ${fadeIn} 1s 0.5s forwards;
  margin: 0;
  line-height: 1.4;
  text-align: center;
  max-width: 90%;
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ProgressBarContainer = styled.div`
  width: 350px;
  height: 10px;
  background-color: #ddd;
  border-radius: 5px;
  overflow: hidden;
  opacity: 0;
  animation: ${fadeIn} 1s 0.5s forwards;
  margin-top: 15px;
  @media (max-width: 768px) {
    width: 80%;
  }
`;

const ProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #0077cc, #8e24aa);
  width: ${(props) => props.progress}%;
  transition: width 0.5s ease-in-out;
`;

const loadingMessages = [
  'Iniciando a conexão com o servidor de dados...',
  'Buscando as informações mais recentes...',
  'Processando os filtros de segurança...',
  'Renderizando o dashboard para você...',
  'Quase lá! Preparando a visualização final...',
];

const PowerBILoading = ({ status, progress, message }) => {
  const defaultMessage =
    status === 'error'
      ? '❌ Erro ao carregar o dashboard. Por favor, tente novamente.'
      : message || loadingMessages[0];
  const imagePath = '/images/logo_so_E.png';
  const spinnerImagePath = '/images/circulo_tes2.svg';

  return (
    <LoadingOverlay>
      <LoadingContainer>
        {status !== 'error' && (
          <>
            <LogoSpinner src={spinnerImagePath} alt="Logo rotativo" />
            <ImageLoader src={imagePath} alt="Logo de carregamento" />
          </>
        )}
      </LoadingContainer>
      <MessageContainer>
        <LoadingText status={status}>{defaultMessage}</LoadingText>
        {status !== 'error' && (
          <ProgressBarContainer>
            <ProgressBar progress={progress} />
          </ProgressBarContainer>
        )}
      </MessageContainer>
    </LoadingOverlay>
  );
};

export default function DashboardHome() {
  const { user } = useContext(CoreContext);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [powerBILoadingStatus, setPowerBILoadingStatus] = useState('loading');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(loadingMessages[0]);
  const embedRef = useRef(null);
  const powerbiService = useRef(null);
  const messageIntervalRef = useRef(null);

  const embedRefValue = embedRef.current;

  useEffect(() => {
    const embedPowerBI = async () => {
      try {
        setPowerBILoadingStatus('loading');
        setLoadingProgress(0);
        setLoadingMessage(loadingMessages[0]);
        if (messageIntervalRef.current) {
          clearInterval(messageIntervalRef.current);
        }
        let messageIndex = 0;
        messageIntervalRef.current = setInterval(() => {
          messageIndex = (messageIndex + 1) % loadingMessages.length;
          setLoadingMessage(loadingMessages[messageIndex]);
        }, 2500);
        const token = await PbToken();
        const accessToken = token.token;
        powerbiService.current = new service.Service(
          factories.hpmFactory,
          factories.wpmpFactory,
          factories.routerFactory
        );
        //verifica se o usuario é admin e se for traz o valor de "Sim"
        let admin = user?.administrador;
        if (admin === true) {
          admin = 'Sim';
        } else {
          admin = 'Não';
        }
        //dashboard oficial.
        const embedUrl =
          'https://app.powerbi.com/reportEmbed?reportId=88fe5b50-5e18-47f1-8d73-e8a6c881333e&groupId=5dd5a7de-22f2-475d-a21f-01fab12aa6b3&w=2&config=eyJjbHVzdGVyVXJsIjoiaHR0cHM6Ly9XQUJJLUJSQVpJTC1TT1VUSC1CLVBSSU1BUlktcmVkaXJlY3QuYW5hbHlzaXMud2luZG93cy5uZXQiLCJlbWJlZEZlYXR1cmVzIjp7InVzYWdlTWV0cmljc1ZOZXh0Ijp0cnVlfX0%3d';
        const embedReportId = '88fe5b50-5e18-47f1-8d73-e8a6c881333e';

        // dashboard de desenvolvimento
        //const embedUrl = "https://app.powerbi.com/reportEmbed?reportId=a0157103-bcb5-40f3-81ca-f06d89d2bce0&groupId=5dd5a7de-22f2-475d-a21f-01fab12aa6b3&w=2&config=eyJjbHVzdGVyVXJsIjoiaHR0cHM6Ly9XQUJJLUJSQVpJTC1TT1VUSC1CLVBSSU1BUlktcmVkaXJlY3QuYW5hbHlzaXMud2luZG93cy5uZXQiLCJlbWJlZEZlYXR1cmVzIjp7InVzYWdlTWV0cmljc1ZOZXh0Ijp0cnVlfX0%3d" ;
        // id do desenvolvi
        //const embedReportId = "a0157103-bcb5-40f3-81ca-f06d89d2bce0";

        const permissions = models.Permissions.All;
        const embedConfig = {
          pageView: 'fitToWidth',
          type: 'report',
          id: embedReportId,
          embedUrl: embedUrl,
          accessToken: accessToken,
          tokenType: '0' ? models.TokenType.Aad : models.TokenType.Embed,
          permissions: permissions,
          settings: {
            panes: {
              filters: { visible: false },
              pageNavigation: { visible: false },
            },
            bars: {
              statusBar: { visible: false },
            },
          },
        };
        //filtro para usuario que nao sao admin, para trazer somente as informacoes de seu CNPJ e nao deixar apertar o botao de relatorio
        const filter = {
          $schema: 'http://powerbi.com/product/schema#basic',
          target: {
            table: 'd_distribuidor',
            column: 'cnpj',
          },
          operator: 'In',
          values: [user.distribuidor.cnpj],
          filterType: models.FilterType.BasicFilter,
          requireSingleSelection: true,
        };
        //filtro para os usuarios Admin que libera o botao de relatorio
        const filterAdmin = {
          $schema: 'http://powerbi.com/product/schema#basic',
          target: {
            table: 'd_usuarios',
            column: 'Admin',
          },
          operator: 'In',
          values: [admin],
          filterType: models.FilterType.BasicFilter,
          requireSingleSelection: true,
        };
        if (embedRefValue) {
          const report = powerbiService.current.embed(
            embedRefValue,
            embedConfig
          );
          report.on('loaded', () => {
            setLoadingProgress(50);
            setLoadingMessage('Aplicando filtros de segurança...');
            //seleciona o filtro para execução
            try {
              if (!user?.administrador) {
                report.updateFilters(models.FiltersOperations.Add, [filter]);
              } else {
                report.updateFilters(models.FiltersOperations.Add, [
                  filterAdmin,
                ]);
              }
            } catch (errors) {
              console.error('Erro ao aplicar o filtro:', errors);
            }
          });
          report.on('rendered', () => {
            clearInterval(messageIntervalRef.current);
            setLoadingProgress(100);
            setLoadingMessage('✅ Dashboard carregado com sucesso!');
            setTimeout(() => {
              setPowerBILoadingStatus('success');
            }, 500);
          });
          report.on('error', (event) => {
            clearInterval(messageIntervalRef.current);
            console.error('Erro no Power BI:', event.detail);
            setPowerBILoadingStatus('error');
            setLoadingMessage(
              '❌ Erro ao carregar o dashboard. Por favor, tente novamente.'
            );
          });
        }
      } catch (error) {
        clearInterval(messageIntervalRef.current);
        console.error('Erro fatal ao carregar o dashboard:', error);
        setPowerBILoadingStatus('error');
        setLoadingMessage(
          '❌ Erro fatal ao carregar o dashboard. Por favor, tente novamente.'
        );
      }
    };
    embedPowerBI();
    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
      if (embedRefValue && powerbiService.current) {
        powerbiService.current.reset(embedRefValue);
      }
    };
  }, [user, embedRefValue]);

  useEffect(() => {
    const adjustReportSize = () => {
      if (embedRefValue) {
        embedRefValue.style.height = document.fullscreenElement
          ? '100dvh'
          : '80dvh';
        embedRefValue.style.width = '100%';
      }
    };
    document.addEventListener('fullscreenchange', adjustReportSize);
    window.addEventListener('resize', adjustReportSize);
    return () => {
      document.removeEventListener('fullscreenchange', adjustReportSize);
      window.addEventListener('resize', adjustReportSize);
    };
  }, [embedRefValue]);

  const handleFullscreen = () => {
    const powerbiContainer = document.getElementById('powerbi-container');
    if (powerbiContainer) {
      if (!document.fullscreenElement) {
        powerbiContainer.requestFullscreen().catch((err) => {
          console.error(
            `Erro ao tentar ativar o modo tela cheia: ${err.message}`
          );
        });
      } else {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const fullscreenChangeHandler = () =>
      setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', fullscreenChangeHandler);
    return () => {
      document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
    };
  }, []);

  const renderFullscreenIcon = () => {
    if (isFullscreen) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 3v3a2 2 0 0 1-2 2H3m3-5 5 5M16 3v3a2 2 0 0 0 2 2h3m-3-5-5 5M8 21v-3a2 2 0 0 1-2-2H3m3 5-5-5M16 21v-3a2 2 0 0 0 2-2h3m-3 5 5-5" />
        </svg>
      );
    }
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 8V4a2 2 0 0 1 2-2h4M8 4l-4 4M20 8V4a2 2 0 0 0-2-2h-4M16 4l4 4M4 16v4a2 2 0 0 0 2 2h4M8 20l-4-4M20 16v4a2 2 0 0 1-2 2h-4M16 20l4-4" />
      </svg>
    );
  };

  return (
    <ContainerAuthenticated>
      <div
        id="powerbi-container"
        style={{
          position: 'relative',
          height: '92dvh',
          width: '100%',
        }}
      >
        {powerBILoadingStatus !== 'success' && (
          <PowerBILoading
            status={powerBILoadingStatus}
            progress={loadingProgress}
            message={loadingMessage}
          />
        )}
        <FullscreenButton
          onClick={handleFullscreen}
          title="Alternar modo de tela cheia"
        >
          {renderFullscreenIcon()}
        </FullscreenButton>
        <div
          ref={embedRef}
          style={{
            height: '92dvh',
            width: '100%',
            display: powerBILoadingStatus === 'success' ? 'block' : 'none',
          }}
        />
      </div>
    </ContainerAuthenticated>
  );
}
